/**
 * Step 1 — the sale and the new home (downsizing rebuild, 21 Oct 2026; the agreed mock-up's j1-step1).
 *
 * Top to bottom: the primer, shown once (the downsizing trap, buying dearer, stamp duty, the downsizer
 * contribution); the sale, prefilled from the profile; buy another home, rent instead, or compare both — asked
 * here, not in a pop-over (review decision 2); the new home's price, or "test a range"; the weekly rent; the
 * state, from the file where it is recorded, with the stamp duty for every price tested shown on the step
 * (review decision 1: duty is never silently off). Every answer is kept on the file (goals.dz*), so a return
 * never resets it.
 */
import React, { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { STAMP_DUTY_STATES, isStampDutyState } from '../../../../shared/src/calculation/stampDuty.ts';
import { DOWNSIZER_CONTRIBUTION_CAP } from '../../constants/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { dzDutyFor, dzDefaultNewHomeState, dzSaleFigures } from './dzSaleFigures.ts';
import { dzPriceWords } from './dzChosenPlan.ts';
import type { DzHousing } from './downsizerScenario.tsx';
import { PrimerHeader, PRIMER_RAIL, PRIMER_LEAD, PRIMER_FIGURE } from '../../components/strategy/PrimerHeader.tsx';

export interface DzSale { salePrice: number; sellingCosts: number; date: string }

/** The prices a "test a range" run tries, as the engine builds them (downsizerScenario.tsx's ladder). */
export function dzPricesTested(salePrice: number, sellingCosts: number, namedPrice: number): Array<{ label: string; price: number }> {
    if (namedPrice > 0) return [{ label: 'the home', price: Math.round(namedPrice) }];
    const net = Math.max(0, salePrice - sellingCosts);
    const out = [
        { label: 'a cheaper home', price: Math.round(salePrice * 0.6) },
        { label: 'a smaller home', price: Math.round(salePrice * 0.7) },
        { label: 'a home at a similar price', price: Math.round(net) },
        { label: 'a dearer home', price: Math.round(salePrice * 1.15) },
    ];
    return out.filter((p) => p.price > 0);
}

/** The state the file says, and where it came from ("From the profile's address", "From the village setup"). */
export function dzStateOf(factFind: DraftFactFind): { code: string; fromFile: boolean } {
    const g = (factFind?.goals || {}) as { dzNewHomeState?: string };
    if (typeof g.dzNewHomeState === 'string') return { code: isStampDutyState(g.dzNewHomeState) ? g.dzNewHomeState : '', fromFile: false };
    const d = dzDefaultNewHomeState(factFind);
    return { code: d, fromFile: !!d };
}

interface DzStep1Props {
    factFind: DraftFactFind;
    setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    sale: DzSale;
    onSale: (patch: Partial<DzSale>) => void;
    onNext: () => void;
    onReadGuide: () => void;
}

const card = 'rounded-2xl border border-slate-200 bg-white p-6 shadow-card';
const label = 'text-[13px] font-semibold text-slate-600';
const input = 'rounded-lg border border-slate-300 bg-white px-3 py-2 text-[15px] font-semibold text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500';
const link = 'font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy';

const Choice = <T extends string>({ value, options, onChange, testId }: { value: T; options: Array<{ id: T; label: string }>; onChange: (id: T) => void; testId?: string }) => (
    <div className="flex flex-wrap gap-2" role="radiogroup" data-testid={testId}>
        {options.map((o) => (
            <button key={o.id} type="button" role="radio" aria-checked={value === o.id} onClick={() => onChange(o.id)} data-testid={testId ? `${testId}-${o.id}` : undefined}
                className={`rounded-lg border px-3.5 py-2 text-[14px] font-semibold ${value === o.id ? 'border-blue-600 bg-blue-50 text-brand-navy' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300'}`}>
                {value === o.id ? '✓ ' : ''}{o.label}
            </button>
        ))}
    </div>
);

const DzStep1 = ({ factFind, setFactFind, sale, onSale, onNext, onReadGuide }: DzStep1Props) => {
    const v = householdVoice(factFind);
    const goals = (factFind?.goals || {}) as Record<string, unknown>;
    const setGoal = (patch: Record<string, unknown>) => setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), ...patch } }));
    const homeValue = parseFloat(String(factFind?.home?.value ?? '')) || 0;
    const housing: DzHousing = goals.dzHousing === 'rent' || goals.dzHousing === 'both' ? goals.dzHousing : 'buy';
    const buys = housing !== 'rent';
    const rents = housing !== 'buy';
    const named = Number(goals.dzNewHomePrice) > 0 ? Number(goals.dzNewHomePrice) : 0;
    const [rangeMode, setRangeMode] = React.useState(!(named > 0));
    const rent = Number(goals.dzRentPerWeek) > 0 ? Number(goals.dzRentPerWeek) : 0;
    const st = dzStateOf(factFind);
    const stateName = STAMP_DUTY_STATES.find((s) => s.code === st.code)?.name ?? '';
    const { netProceeds } = dzSaleFigures({ salePrice: sale.salePrice, sellingCosts: sale.sellingCosts, newHomePrice: 0, stampDuty: 0, sellAndRent: true });
    const prices = dzPricesTested(sale.salePrice, sale.sellingCosts, rangeMode ? 0 : named);
    const similar = Math.round(netProceeds);
    const similarDuty = st.code ? dzDutyFor(st.code, similar) : 0;
    const rentMissing = rents && !(rent > 0);
    const [showExemption, setShowExemption] = React.useState(false);

    // The primer is shown once, then folds to one line that opens it again.
    const primerSeen = goals.dzPrimerSeen === true;
    const [primerOpen, setPrimerOpen] = React.useState(!primerSeen);
    useEffect(() => {
        if (!primerSeen) setGoal({ dzPrimerSeen: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The guide links: under the open primer, beside "Show" when it is folded (23 Oct 2026).
    const primerLinks = <><button type="button" onClick={onReadGuide} className={link}>Read the full guide</button>{' · '}
                    <button type="button" onClick={() => setShowExemption((o) => !o)} aria-expanded={showExemption} className={link} data-testid="dz-exemption-link">The 24-month exemption while {v.you ? 'you buy' : `${v.they} buy`}</button></>;
    return (
        <div className="space-y-5" data-testid="dz-step1">
            <section className={`${card} ${PRIMER_RAIL}`} data-testid="dz-primer">
                <PrimerHeader title="Downsizing, in a minute" open={primerOpen} onToggle={() => setPrimerOpen((o) => !o)} aside={primerLinks} testId="dz-primer-toggle" />
                {primerOpen && (
                    <div className="mt-3 grid grid-cols-1 gap-x-7 gap-y-2 text-[14px] leading-relaxed text-slate-700 md:grid-cols-2">
                        <p><strong className={PRIMER_LEAD}>The home isn&apos;t counted for the Age Pension; money is.</strong> Buy cheaper and the money {v.you ? 'you free' : 'the sale frees'} is counted. That is the downsizing trap.</p>
                        <p><strong className={PRIMER_LEAD}>Buy dearer, and the reverse happens:</strong> savings move into a home that isn&apos;t counted, and the pension can rise.</p>
                        <p><strong className={PRIMER_LEAD}>Stamp duty and selling costs are gone on the day.</strong> {stateName && similarDuty > 0 ? <>In {stateName} the duty on a {dzPriceWords(similar)} home is about <span className={PRIMER_FIGURE}>{formatCurrency(Math.round(similarDuty))}</span>.</> : <>Choose the state below and we work out the duty on every price.</>}</p>
                        <p><strong className={PRIMER_LEAD}>From 55, each of {v.you ? 'you' : v.them} can put up to {formatCurrency(DOWNSIZER_CONTRIBUTION_CAP)} of the sale into super</strong> as a downsizer contribution, if the home was owned for 10 years or more.</p>
                    </div>
                )}
                {primerOpen && <p className="mt-3 text-[13px] text-slate-500">{primerLinks}</p>}
                {showExemption && (
                    <p className="mt-2 rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-white px-3 py-2 text-[13.5px] leading-relaxed text-slate-700" data-testid="dz-exemption">
                        If {v.they} sell before buying, the part of the sale {v.they} mean to spend on the new home is <strong>not counted for the Age Pension for up to 24 months</strong> (36 if the delay is outside {v.their} control). It is still deemed to earn income, at the lower rate. Tell Services Australia within 14 days of the sale.
                    </p>
                )}
            </section>

            <section className={card} data-testid="dz-sale">
                <p className="text-[12px] font-semibold text-brand-navy">The sale</p>
                <div className="mt-3 grid grid-cols-1 gap-x-7 gap-y-4 md:grid-cols-3">
                    <div>
                        <p className={label}>Sale price</p>
                        <CurrencyInput value={sale.salePrice || ''} onChange={(x) => onSale({ salePrice: Math.max(0, x ?? 0) })} className="mt-1 w-44" ariaLabel="Sale price" testId="dz-sale-price" />
                        <p className="mt-1 text-[12.5px] text-slate-500">{homeValue > 0 && Math.round(sale.salePrice) === Math.round(homeValue) ? 'From the profile' : homeValue > 0 ? `The profile says ${formatCurrency(homeValue)}` : 'No home value on the profile'}</p>
                    </div>
                    <div>
                        <p className={label}>Selling costs</p>
                        <CurrencyInput value={sale.sellingCosts ?? ''} onChange={(x) => onSale({ sellingCosts: Math.max(0, x ?? 0) })} className="mt-1 w-44" ariaLabel="Selling costs" testId="dz-selling-costs" />
                        <p className="mt-1 text-[12.5px] text-slate-500">{sale.salePrice > 0 ? `${(Math.round((sale.sellingCosts / sale.salePrice) * 1000) / 10).toLocaleString('en-AU')}%` : '2.5%'}: agent, legal and moving. Change it if {v.you ? 'you have' : `${v.they} have`} a quote.</p>
                    </div>
                    <div>
                        <label htmlFor="dz-event-date" className={label}>When?</label>
                        <input id="dz-event-date" type="date" value={sale.date} onChange={(e) => onSale({ date: e.target.value })} className={`${input} mt-1 block font-normal`} />
                        <p className="mt-1 text-[12.5px] text-slate-500">{sale.date ? (sale.date < new Date().toISOString().slice(0, 10) ? 'A past date models as "now".' : 'Life as it is until then, then the move.') : 'Now. Leave it blank while still deciding.'}</p>
                    </div>
                </div>

                <p className="mt-6 text-[12px] font-semibold text-brand-navy">After the sale</p>
                <div className="mt-2">
                    <Choice<DzHousing> value={housing} onChange={(id) => setGoal({ dzHousing: id })} testId="dz-housing" options={[
                        { id: 'buy', label: 'Buy another home' },
                        { id: 'rent', label: 'Rent instead' },
                        { id: 'both', label: 'Compare both' },
                    ]} />
                    <p className="mt-2 text-[13px] text-slate-600">Buying keeps a home out of the Age Pension test. Renting frees the most money, but every dollar is then counted, and the rent is a cost for life.</p>
                </div>

                {buys && (
                    <>
                        <p className="mt-6 text-[12px] font-semibold text-brand-navy">The new home</p>
                        <div className="mt-2 flex flex-wrap items-start gap-x-6 gap-y-3">
                            <div>
                                <p className={label}>Price, if {v.you ? 'you have' : `${v.they} have`} one in mind</p>
                                <CurrencyInput value={rangeMode ? '' : (named || '')} onChange={(x) => { const p = Math.max(0, x ?? 0); setRangeMode(!(p > 0)); setGoal({ dzNewHomePrice: p }); }} placeholder="e.g. 1,950,000" className="mt-1 w-48" ariaLabel="The new home's price" testId="dz-new-home-price" />
                            </div>
                            <label className="mt-6 flex cursor-pointer items-start gap-2 text-[14px] text-slate-700">
                                <input type="checkbox" checked={rangeMode} onChange={(e) => { setRangeMode(e.target.checked); if (e.target.checked) setGoal({ dzNewHomePrice: 0 }); }} className="mt-1 h-4 w-4 accent-blue-600" data-testid="dz-test-range" />
                                <span><strong className="text-slate-900">Not sure — test a range.</strong> We test a cheaper, a similar and a dearer home{prices.length > 1 ? `: ${prices.map((p) => dzPriceWords(p.price).replace(' million', 'm')).join(', ')}` : ''}.</span>
                            </label>
                        </div>
                    </>
                )}

                {rents && (
                    <div className="mt-6">
                        <p className="text-[12px] font-semibold text-brand-navy">The rent</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-slate-600">
                            <CurrencyInput value={rent || ''} onChange={(x) => setGoal({ dzRentPerWeek: Math.max(0, x ?? 0) })} className="w-36" ariaLabel="Rent a week" testId="dz-rent" />
                            <span>a week{rent > 0 ? ` (about ${formatCurrency(Math.round(rent * 52))} a year, rising with inflation; Rent Assistance is assessed on it)` : ''}</span>
                        </div>
                        {rentMissing && <p className="mt-1 text-[13px] text-amber-800" data-testid="dz-rent-missing">Enter the weekly rent: it is charged every year and rises with prices, so without it renting looks free.</p>}
                    </div>
                )}

                {buys && (
                    <div className="mt-6" data-testid="dz-state">
                        <div className="flex flex-wrap items-center gap-3">
                            <label htmlFor="dz-state-select" className="text-[12px] font-semibold text-brand-navy">State</label>
                            <select id="dz-state-select" value={st.code} onChange={(e) => setGoal({ dzNewHomeState: e.target.value })} className={input} data-testid="dz-state-select">
                                <option value="">Not sure — no stamp duty in the figures</option>
                                {STAMP_DUTY_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                            </select>
                            <span className="text-[13px] text-slate-500">{st.code ? (st.fromFile ? 'From what the file already records. ' : '') : ''}{st.code ? 'Stamp duty is worked out for every price we test.' : ''}</span>
                        </div>
                        {st.code ? (
                            <table className="mt-3 w-full max-w-xl text-[14px]" data-testid="dz-duty-table">
                                <tbody>
                                    {prices.map((p) => (
                                        <tr key={p.label} className="border-b border-slate-100">
                                            <td className="py-1.5 text-slate-700">Stamp duty on a {formatCurrency(p.price)} home</td>
                                            <td className="py-1.5 text-right font-bold tabular-nums text-slate-900">{formatCurrency(Math.round(dzDutyFor(st.code, p.price)))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="mt-2 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-3 py-2 text-[13.5px] text-slate-700" data-testid="dz-no-state">
                                <strong>No state, no stamp duty.</strong> Without the state the figures leave the duty out, which makes buying — a dearer home most of all — look better than it is. Every plan will say so.
                            </p>
                        )}
                        <p className="mt-1 text-[12.5px] text-slate-500">The state&apos;s standard scale for a home to live in, with no concessions. A conveyancer confirms the real figure.</p>
                    </div>
                )}
            </section>

            <div className="flex flex-wrap items-center justify-end gap-3">
                {rentMissing && <span className="text-[13px] text-slate-500">Enter the rent to go on.</span>}
                <button type="button" onClick={onNext} disabled={!(sale.salePrice > 0) || rentMissing} data-testid="dz-step1-next"
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-5 text-[14px] font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50">
                    See what this means for {v.them} <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default DzStep1;
